/**
 * Fetch PDF blobs and open the browser print dialog for each, one after another.
 * Does not trigger file downloads — blobs stay in memory as object URLs.
 *
 * Direct PDF-in-iframe (Chrome shows the dialog). Completion uses afterprint,
 * focus/input after a short settle, and matchMedia("print") — Chrome's PDF
 * plugin often skips afterprint and never blurs the opener.
 */

export type PdfPrintSource = {
  /** Human label for errors / progress (optional). */
  label?: string;
  fetchBlob: () => Promise<Blob>;
};

export type PrintPdfBlobsOptions = {
  /** Called while fetching (1-based index) and again while printing. */
  onProgress?: (phase: "fetch" | "print", current: number, total: number) => void;
  /** Max wait after print() before continuing to the next PDF (ms). */
  afterPrintTimeoutMs?: number;
  signal?: AbortSignal;
};

/** Min time after print() before focus/input counts as "dialog closed". */
const PRINT_SETTLE_MS = 700;
/** Default safety net once print has started (Chrome often never fires afterprint). */
const DEFAULT_AFTER_PRINT_TIMEOUT_MS = 12_000;
/** Wait for PDF viewer to attach when iframe was not preloaded. */
const PDF_ATTACH_MS = 350;

function ensurePdfBlob(blob: Blob): Blob {
  if (blob.type === "application/pdf") return blob;
  return new Blob([blob], { type: "application/pdf" });
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

type PreparedPdfFrame = {
  url: string;
  iframe: HTMLIFrameElement;
  /** Resolves when iframe has loaded (or already loaded). */
  ready: Promise<void>;
  revoke: () => void;
};

function stylePrintIframe(iframe: HTMLIFrameElement): void {
  iframe.setAttribute("aria-hidden", "true");
  // Real viewport size (off-screen) — required for Chrome to show the print UI.
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1024px;height:768px;border:0;opacity:0;pointer-events:none;";
}

function preparePdfFrame(blob: Blob): PreparedPdfFrame {
  const url = URL.createObjectURL(ensurePdfBlob(blob));
  const iframe = document.createElement("iframe");
  stylePrintIframe(iframe);

  let resolveReady!: () => void;
  let rejectReady!: (err: unknown) => void;
  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  iframe.onload = () => resolveReady();
  iframe.onerror = () => rejectReady(new Error("Failed to load PDF for printing"));

  document.body.appendChild(iframe);
  iframe.src = url;

  return {
    url,
    iframe,
    ready,
    revoke: () => {
      iframe.remove();
      URL.revokeObjectURL(url);
    },
  };
}

/**
 * Wait until the print dialog is done for a window that already called print(),
 * or call print() on the prepared iframe first.
 */
function awaitPrintDialogClose(
  win: Window,
  options?: { afterPrintTimeoutMs?: number; signal?: AbortSignal },
): Promise<void> {
  const timeoutMs = options?.afterPrintTimeoutMs ?? DEFAULT_AFTER_PRINT_TIMEOUT_MS;

  return new Promise((resolve, reject) => {
    assertNotAborted(options?.signal);

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let settlePassed = false;
    const printStartedAt = Date.now();

    const mql =
      typeof window.matchMedia === "function" ? window.matchMedia("print") : null;

    const onAbort = () => fail(new DOMException("Aborted", "AbortError"));
    options?.signal?.addEventListener("abort", onAbort, { once: true });

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      if (settleTimer !== undefined) clearTimeout(settleTimer);
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
      options?.signal?.removeEventListener("abort", onAbort);
      window.removeEventListener("afterprint", onAfterPrint);
      window.removeEventListener("focus", onUserReturn);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("keydown", onUserReturn, true);
      document.removeEventListener("mousedown", onUserReturn, true);
      try {
        win.removeEventListener("afterprint", onAfterPrint);
      } catch {
        /* ignore */
      }
      try {
        mql?.removeEventListener("change", onPrintMedia);
      } catch {
        /* ignore */
      }
    };

    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const fail = (err: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    const onAfterPrint = () => finish();

    const scheduleFinish = () => {
      if (settled || !settlePassed) return;
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => finish(), 80);
    };

    const onUserReturn = () => scheduleFinish();

    const onVisibility = () => {
      if (document.visibilityState === "visible") scheduleFinish();
    };

    const onPrintMedia = (e: MediaQueryListEvent) => {
      if (!e.matches) scheduleFinish();
    };

    settleTimer = setTimeout(() => {
      settlePassed = true;
    }, PRINT_SETTLE_MS);

    timer = setTimeout(finish, timeoutMs);

    win.addEventListener("afterprint", onAfterPrint);
    window.addEventListener("afterprint", onAfterPrint);
    // Focus/input only count after settle — avoids closing while the dialog is opening.
    window.addEventListener("focus", onUserReturn);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("keydown", onUserReturn, true);
    document.addEventListener("mousedown", onUserReturn, true);
    mql?.addEventListener("change", onPrintMedia);

    void printStartedAt;
  });
}

async function printPreparedFrame(
  prepared: PreparedPdfFrame,
  options?: { afterPrintTimeoutMs?: number; signal?: AbortSignal },
): Promise<void> {
  assertNotAborted(options?.signal);
  await prepared.ready;
  assertNotAborted(options?.signal);

  // Brief beat so the PDF plugin can attach after load.
  await new Promise<void>((r) => setTimeout(r, PDF_ATTACH_MS));
  assertNotAborted(options?.signal);

  const win = prepared.iframe.contentWindow;
  if (!win) {
    throw new Error("PDF print iframe has no contentWindow");
  }

  win.focus();
  win.print();
  await awaitPrintDialogClose(win, options);
}

/**
 * Print a single PDF blob via a hidden-but-sized iframe.
 * Zero-size iframes often suppress the print dialog in Chromium.
 */
export function printOnePdfBlob(
  blob: Blob,
  options?: { afterPrintTimeoutMs?: number; signal?: AbortSignal },
): Promise<void> {
  const prepared = preparePdfFrame(blob);
  return printPreparedFrame(prepared, options).finally(() => {
    prepared.revoke();
  });
}

/**
 * Fetch all sources (in parallel), then print each PDF sequentially.
 * While one dialog is open, the next PDF iframe is already warming up.
 */
export async function printPdfBlobs(
  sources: PdfPrintSource[],
  options?: PrintPdfBlobsOptions,
): Promise<void> {
  if (sources.length === 0) return;

  const total = sources.length;
  assertNotAborted(options?.signal);

  let fetchDone = 0;
  const blobs = await Promise.all(
    sources.map(async (source, index) => {
      assertNotAborted(options?.signal);
      const raw = await source.fetchBlob();
      fetchDone += 1;
      options?.onProgress?.("fetch", fetchDone, total);
      if (!raw || raw.size === 0) {
        const label = source.label ?? `#${index + 1}`;
        throw new Error(`Empty PDF: ${label}`);
      }
      return ensurePdfBlob(raw);
    }),
  );

  const frames: Array<PreparedPdfFrame | null> = blobs.map(() => null);

  const ensureFrame = (index: number): PreparedPdfFrame => {
    let frame = frames[index];
    if (!frame) {
      frame = preparePdfFrame(blobs[index]);
      frames[index] = frame;
    }
    return frame;
  };

  try {
    // Warm the first frame before the print loop.
    ensureFrame(0);

    for (let i = 0; i < blobs.length; i++) {
      assertNotAborted(options?.signal);
      options?.onProgress?.("print", i + 1, total);

      // Preload the next PDF while the current dialog is open.
      if (i + 1 < blobs.length) {
        ensureFrame(i + 1);
      }

      const current = ensureFrame(i);
      await printPreparedFrame(current, {
        afterPrintTimeoutMs: options?.afterPrintTimeoutMs,
        signal: options?.signal,
      });
      current.revoke();
      frames[i] = null;
    }
  } finally {
    for (const frame of frames) {
      frame?.revoke();
    }
  }
}
